import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';

/**
 * Travel Cari Mizan — printable report (A4 portrait).
 *
 * Modeled on the legacy ProAcenta "TARİH ARALIĞI CARİ MİZAN" PDF, redesigned:
 * letterhead with logo, parameter strip, currency bands, cari-type groups,
 * 4-column figures (Borç/Alacak turnover + Borç/Alacak Bakiye), group and
 * currency totals with a signed BAKİYE line, page footer with numbering.
 *
 * Uses Roboto (public/fonts) — full Turkish glyph coverage, unlike the
 * built-in Helvetica.
 */

/**
 * Register the report font. Defaults to the app's public /fonts path (browser);
 * tests/scripts may re-register with a filesystem base path (last call wins).
 */
export function registerMizanFonts(basePath = '/fonts'): void {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: `${basePath}/Roboto-Regular.ttf` },
      { src: `${basePath}/Roboto-Medium.ttf`, fontWeight: 500 },
      { src: `${basePath}/Roboto-Bold.ttf`, fontWeight: 700 },
    ],
  });
}
// Browser: register from the app's public path at module load. Node scripts
// call registerMizanFonts() themselves with a filesystem base path.
if (typeof window !== 'undefined') registerMizanFonts();
// Turkish words must never be auto-hyphenated.
Font.registerHyphenationCallback((word) => [word]);

const COMPANY = {
  name: 'POLATKAN TURİZM',
  address: 'Gazeteci Kemal Togan Sk, Lefkoşa 99010',
  phone: '(0392) 444 02 22',
  email: 'info@polatkanturizm.com',
  web: 'www.polatkan.net',
};

const C = {
  ink: '#111827',
  inkSoft: '#4b5563',
  inkFaint: '#9ca3af',
  navy: '#1e3a5f',
  navySoft: '#eef2f7',
  band: '#f3f4f6',
  zebra: '#fafbfc',
  line: '#e5e7eb',
  lineSoft: '#f1f5f9',
  green: '#047857',
  red: '#b91c1c',
  white: '#ffffff',
};

const s = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 54,
    paddingHorizontal: 36,
    fontSize: 8.5,
    fontFamily: 'Roboto',
    color: C.ink,
  },

  // ── Letterhead ──────────────────────────────────────────────────────────
  letterhead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 14,
    marginBottom: 14,
    borderBottomWidth: 2,
    borderBottomColor: C.navy,
  },
  brand: { flexDirection: 'row', alignItems: 'center' },
  logo: { width: 42, height: 42, marginRight: 10 },
  companyName: { fontSize: 15, fontWeight: 700, color: C.navy, letterSpacing: 0.4 },
  companyLine: { fontSize: 7.5, color: C.inkSoft, marginTop: 1.5 },
  reportMeta: { alignItems: 'flex-end' },
  reportTitle: { fontSize: 14, fontWeight: 700, color: C.navy, letterSpacing: 0.6 },
  reportPeriod: { fontSize: 9, fontWeight: 500, color: C.ink, marginTop: 3 },
  reportGenerated: { fontSize: 7, color: C.inkFaint, marginTop: 3 },

  // ── Parameter strip ─────────────────────────────────────────────────────
  paramStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: C.navySoft,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: C.navy,
  },
  paramItem: { fontSize: 7.5, color: C.inkSoft, marginRight: 12 },
  paramValue: { fontWeight: 500, color: C.ink },

  // ── Table ───────────────────────────────────────────────────────────────
  currencyBand: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: C.navy,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 10,
  },
  currencyBandText: { color: C.white, fontSize: 9, fontWeight: 700, letterSpacing: 0.5 },

  headerRow: {
    flexDirection: 'row',
    backgroundColor: C.navySoft,
    borderBottomWidth: 1,
    borderBottomColor: C.navy,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  headerCell: { fontSize: 7.5, fontWeight: 700, color: C.navy },

  groupBand: {
    backgroundColor: C.band,
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.line,
  },
  groupBandText: { fontSize: 7.5, fontWeight: 700, color: C.inkSoft, letterSpacing: 0.5 },

  row: {
    flexDirection: 'row',
    paddingVertical: 3.5,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.lineSoft,
  },
  rowAlt: { backgroundColor: C.zebra },
  cell: { fontSize: 8 },
  cellMuted: { color: C.inkSoft },
  num: { textAlign: 'right' },

  groupTotalRow: {
    flexDirection: 'row',
    backgroundColor: C.navySoft,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderTopWidth: 0.5,
    borderTopColor: C.line,
  },
  groupTotalCell: { fontSize: 8, fontWeight: 700, color: C.ink },

  currencyTotalRow: {
    flexDirection: 'row',
    backgroundColor: C.navy,
    paddingVertical: 5,
    paddingHorizontal: 6,
    marginTop: 2,
  },
  currencyTotalCell: { fontSize: 8.5, fontWeight: 700, color: C.white },

  bakiyeRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.line,
  },
  bakiyeText: { fontSize: 9, fontWeight: 700 },

  // ── Column widths ───────────────────────────────────────────────────────
  colNo: { width: '4%' },
  colCode: { width: '9%' },
  colName: { width: '35%' },
  colNum: { width: '13%' },

  // ── Footer ──────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: C.line,
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: C.inkFaint },
});

const fmt = (n: number): string =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtCell = (n: number): string => (Math.abs(n) < 0.005 ? '—' : fmt(n));

export interface MizanPdfRow {
  cariCode: string;
  companyName: string;
  cariType: string;
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
}

export interface MizanPdfTotals {
  debit: number;
  credit: number;
  debitBalance: number;
  creditBalance: number;
  net: number;
}

export interface MizanPdfCurrencySection {
  currency: string;
  cariGroups: { cariType: string; rows: MizanPdfRow[]; totals: MizanPdfTotals }[];
  totals: MizanPdfTotals;
}

export interface CariMizanPDFProps {
  data: {
    fiscalYear: number;
    currencies: MizanPdfCurrencySection[];
  };
  meta: {
    title: string; // "CARİ MİZAN" / "TARİH ARALIĞI CARİ MİZAN"
    periodLabel: string;
    generatedLabel: string; // "Oluşturulma: 05.06.2026 14:21"
    params: { label: string; value: string }[];
    columns: { no: string; code: string; name: string; debit: string; credit: string; debitBalance: string; creditBalance: string };
    totalLabel: string; // "TOPLAMI"
    balanceLabel: string; // "BAKİYE"
    debitSide: string; // "(B)"
    creditSide: string; // "(A)"
    pageLabel: (page: number, total: number) => string;
    currencyNames: Record<string, string>;
    typeLabel: (code: string) => string;
  };
  logoUrl?: string;
}

// Note: the explicit React import + return type keep this component renderable
// under both the app's automatic JSX runtime and Node-side classic transforms
// (used by the preview/test scripts that render the PDF outside the browser).
export function CariMizanPDF({ data, meta, logoUrl }: CariMizanPDFProps): React.JSX.Element {
  return (
    <Document
      title={`${meta.title} ${data.fiscalYear}`}
      author={COMPANY.name}
      subject={meta.periodLabel}
    >
      <Page size="A4" style={s.page}>
        {/* Letterhead */}
        <View style={s.letterhead}>
          <View style={s.brand}>
            {logoUrl && <Image src={logoUrl} style={s.logo} />}
            <View>
              <Text style={s.companyName}>{COMPANY.name}</Text>
              <Text style={s.companyLine}>{COMPANY.address}</Text>
              <Text style={s.companyLine}>
                {COMPANY.phone} · {COMPANY.email} · {COMPANY.web}
              </Text>
            </View>
          </View>
          <View style={s.reportMeta}>
            <Text style={s.reportTitle}>{meta.title}</Text>
            <Text style={s.reportPeriod}>
              {data.fiscalYear} · {meta.periodLabel}
            </Text>
            <Text style={s.reportGenerated}>{meta.generatedLabel}</Text>
          </View>
        </View>

        {/* Parameter strip */}
        <View style={s.paramStrip}>
          {meta.params.map((p) => (
            <Text key={p.label} style={s.paramItem}>
              {p.label}: <Text style={s.paramValue}>{p.value}</Text>
            </Text>
          ))}
        </View>

        {/* Currency sections */}
        {data.currencies.map((cur) => (
          <View key={cur.currency}>
            <View style={s.currencyBand} minPresenceAhead={80}>
              <Text style={s.currencyBandText}>{meta.currencyNames[cur.currency] ?? cur.currency}</Text>
              <Text style={s.currencyBandText}>{cur.currency}</Text>
            </View>

            {/* Column header */}
            <View style={s.headerRow}>
              <Text style={[s.headerCell, s.colNo]}>{meta.columns.no}</Text>
              <Text style={[s.headerCell, s.colCode]}>{meta.columns.code}</Text>
              <Text style={[s.headerCell, s.colName]}>{meta.columns.name}</Text>
              <Text style={[s.headerCell, s.colNum, s.num]}>{meta.columns.debit}</Text>
              <Text style={[s.headerCell, s.colNum, s.num]}>{meta.columns.credit}</Text>
              <Text style={[s.headerCell, s.colNum, s.num]}>{meta.columns.debitBalance}</Text>
              <Text style={[s.headerCell, s.colNum, s.num]}>{meta.columns.creditBalance}</Text>
            </View>

            {(() => {
              let rowNo = 0;
              return cur.cariGroups.map((group) => {
                const startNo = rowNo;
                rowNo += group.rows.length;
                return (
                  <View key={group.cariType}>
                    <View style={s.groupBand} minPresenceAhead={40}>
                      <Text style={s.groupBandText}>
                        {group.cariType} — {meta.typeLabel(group.cariType).toLocaleUpperCase('tr-TR')}
                      </Text>
                    </View>
                    {group.rows.map((row, i) => (
                      <View key={row.cariCode} style={i % 2 === 1 ? [s.row, s.rowAlt] : s.row} wrap={false}>
                        <Text style={[s.cell, s.cellMuted, s.colNo]}>{startNo + i + 1}</Text>
                        <Text style={[s.cell, s.cellMuted, s.colCode]}>{row.cariCode}</Text>
                        <Text style={[s.cell, s.colName]}>{row.companyName}</Text>
                        <Text style={[s.cell, s.colNum, s.num]}>{fmtCell(row.debit)}</Text>
                        <Text style={[s.cell, s.colNum, s.num]}>{fmtCell(row.credit)}</Text>
                        <Text style={[s.cell, s.colNum, s.num]}>{fmtCell(row.debitBalance)}</Text>
                        <Text style={[s.cell, s.colNum, s.num]}>{fmtCell(row.creditBalance)}</Text>
                      </View>
                    ))}
                    <View style={s.groupTotalRow} wrap={false}>
                      <Text style={[s.groupTotalCell, s.colNo]} />
                      <Text style={[s.groupTotalCell, s.colCode]} />
                      <Text style={[s.groupTotalCell, s.colName]}>
                        {group.cariType} {meta.totalLabel}
                      </Text>
                      <Text style={[s.groupTotalCell, s.colNum, s.num]}>{fmt(group.totals.debit)}</Text>
                      <Text style={[s.groupTotalCell, s.colNum, s.num]}>{fmt(group.totals.credit)}</Text>
                      <Text style={[s.groupTotalCell, s.colNum, s.num]}>{fmt(group.totals.debitBalance)}</Text>
                      <Text style={[s.groupTotalCell, s.colNum, s.num]}>{fmt(group.totals.creditBalance)}</Text>
                    </View>
                  </View>
                );
              });
            })()}

            {/* Currency totals + signed balance */}
            <View style={s.currencyTotalRow} wrap={false}>
              <Text style={[s.currencyTotalCell, s.colNo]} />
              <Text style={[s.currencyTotalCell, s.colCode]} />
              <Text style={[s.currencyTotalCell, s.colName]}>
                {cur.currency} {meta.totalLabel}
              </Text>
              <Text style={[s.currencyTotalCell, s.colNum, s.num]}>{fmt(cur.totals.debit)}</Text>
              <Text style={[s.currencyTotalCell, s.colNum, s.num]}>{fmt(cur.totals.credit)}</Text>
              <Text style={[s.currencyTotalCell, s.colNum, s.num]}>{fmt(cur.totals.debitBalance)}</Text>
              <Text style={[s.currencyTotalCell, s.colNum, s.num]}>{fmt(cur.totals.creditBalance)}</Text>
            </View>
            <View style={s.bakiyeRow} wrap={false}>
              <Text style={[s.bakiyeText, { color: cur.totals.net >= 0 ? C.green : C.red }]}>
                {meta.balanceLabel}: {fmt(Math.abs(cur.totals.net))} {cur.totals.net >= 0 ? meta.debitSide : meta.creditSide}
              </Text>
            </View>
          </View>
        ))}

        {/* Footer (every page) */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            {COMPANY.name} · {meta.title} {data.fiscalYear}
          </Text>
          <Text style={s.footerText} render={({ pageNumber, totalPages }) => meta.pageLabel(pageNumber, totalPages)} />
        </View>
      </Page>
    </Document>
  );
}
